import { isDataOutdated } from '_stores/storeHelper';
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export type AvailableBoost ={
  networkId?: string;
  contractId?: number;
  marketId: number;
  productType: string;
  addDate: Date;
  newTotalAvailableBoosts: number;
  newDistributedNotUsedBoosts: number;
}

export type AvailableBoostStore = {
  availableBoosts: AvailableBoost[];
  setAvailableBoosts: (value: AvailableBoost[]) => void;
  findAvailableBoost: (networkId: string | null, contractId: number | null, marketId: number, productType: string) => AvailableBoost | undefined;
  upsertAvailableBoost: (value: AvailableBoost) => void;
  clearAvailableBoost: (networkId: string | null, contractId: number | null, marketId: number, productType: string) => void;
  removeOutdatedAvailableBoosts: () => void;
};

export const useAvailableBoostStore = create<AvailableBoostStore>()(
  persist(
    (set, get) => ({
        availableBoosts: [],
        setAvailableBoosts: (availableBoosts) => set({availableBoosts}),
        findAvailableBoost: (networkId, contractId, marketId, productType) => {
          const validBoosts = get().availableBoosts?.filter(d => !isDataOutdated(d.addDate));
          const boost = networkId 
            ? validBoosts.find(d => d.networkId === networkId && d.marketId === marketId && d.productType === productType)
            : validBoosts.find(d => d.contractId === contractId && d.marketId === marketId && d.productType === productType);
          return boost;
        },
        upsertAvailableBoost: (availableBoost) => {
          const index = availableBoost.networkId 
            ? get().availableBoosts.findIndex(d => d.networkId === availableBoost.networkId && d.marketId === availableBoost.marketId && d.productType === availableBoost.productType)
            : get().availableBoosts.findIndex(d => d.contractId === availableBoost.contractId && d.marketId === availableBoost.marketId && d.productType === availableBoost.productType);

          if (index !== -1) {
            set(state => {
              const newBoosts = [...state.availableBoosts];
              newBoosts[index] = availableBoost;
              return { availableBoosts: newBoosts };
            });
          } else {
            set(state => ({ availableBoosts: [...state.availableBoosts, availableBoost] }));
          }
        },
        clearAvailableBoost: (networkId, contractId, marketId, productType) => {
          const boosts = networkId 
            ? get().availableBoosts?.filter(d => !(d.networkId === networkId && d.marketId === marketId && d.productType === productType))
            : get().availableBoosts?.filter(d => !(d.contractId === contractId && d.marketId === marketId && d.productType === productType));
          set({ availableBoosts: boosts });
        },
        removeOutdatedAvailableBoosts:() => {
          const outdatedAvailableBoost = get().availableBoosts?.filter(a => isDataOutdated(a.addDate));
          outdatedAvailableBoost?.forEach(d => get().clearAvailableBoost(d.networkId, d.contractId, d.marketId, d.productType));
        }
    }),
    {
      name: 'availableBoosts',
      storage: createJSONStorage(() => localStorage),
    }
  )
)
